import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdShoppingBag,
  MdAccountBalanceWallet,
  MdTrendingUp,
  MdTrendingDown,
  MdLocalShipping,
  MdReceiptLong,
  MdSavings,
  MdInsights,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/sales-revenue';
const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'profit_desc', label: 'Profit: high to low' },
  { value: 'profit_asc', label: 'Profit: low to high' },
  { value: 'net_desc', label: 'Net revenue: high to low' },
  { value: 'net_asc', label: 'Net revenue: low to high' },
];

const COLUMNS = [
  { key: 'orderNumber', label: 'Order Number' },
  { key: 'orderDate', label: 'Order Date', format: fmtDate },
  { key: 'customer', label: 'Customer' },
  { key: 'orderValue', label: 'Order Value', numeric: true, format: inr },
  { key: 'productsCount', label: 'Products', numeric: true, center: true },
  { key: 'awbCost', label: 'AWB Cost', numeric: true, format: inr },
  { key: 'netRevenue', label: 'Net Revenue', numeric: true, format: inr },
  { key: 'profit', label: 'Profit (+/-)', numeric: true },
];

const SalesRevenueReport = () => {
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [profitType, setProfitType] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { sortBy };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (profitType !== 'all') p.profitType = profitType;
    return p;
  }, [from, to, search, profitType, sortBy]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };

  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };

  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setProfitType('all');
    setSortBy('date_desc');
    setPage(0);
  };

  const fetchAllRows = async () => {
    const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
    if (!res?.success) throw new Error(res?.message || 'Export failed');
    return res.rows || [];
  };

  const exportRowsAsObjects = (rows) =>
    rows.map((r) => ({
      'Order Number': r.orderNumber,
      'Order Date': fmtDate(r.orderDate),
      Customer: r.customer,
      'Order Value': r.orderValue,
      'Products Count': r.productsCount,
      'AWB Cost': r.awbCost,
      'Net Revenue': r.netRevenue,
      'Profit (+/-)': r.profit,
    }));

  const fileStamp = () => `Sales_Revenue_Report_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const rows = await fetchAllRows();
      if (kind === 'csv') downloadCsv(exportRowsAsObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportRowsAsObjects(rows), fileStamp(), 'Sales & Revenue');
      else
        openPrintView({
          title: 'Sales & Revenue Report',
          subtitle: `${rangeLabel(from, to)} · ${rows.length} orders`,
          headers: COLUMNS.map((c) => c.label),
          cells: rows.map((r) =>
            COLUMNS.map((c) => {
              if (c.key === 'profit') return { value: inr(r.profit), className: r.profit >= 0 ? 'pos' : 'neg' };
              return { value: c.format ? c.format(r[c.key]) : r[c.key], className: c.numeric ? 'num' : '' };
            })
          ),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.rows || [];

  const cards = [
    { label: 'Total Orders', value: (stats.totalOrders || 0).toLocaleString('en-IN'), icon: MdShoppingBag, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${stats.profitableOrders || 0} profitable · ${stats.lossOrders || 0} loss` },
    { label: 'Order Value', value: inr(stats.orderValue), icon: MdReceiptLong, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: `Avg ${inr(stats.avgOrderValue)} per order` },
    { label: 'Revenue', value: inr(stats.revenue), icon: MdAccountBalanceWallet, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'Collected minus delivery charges' },
    {
      label: 'Profit (+/-)',
      value: inr(stats.profit),
      icon: (stats.profit || 0) >= 0 ? MdTrendingUp : MdTrendingDown,
      gradient: (stats.profit || 0) >= 0 ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#f43f5e,#dc2626)',
      sub: 'Estimated from average purchase cost',
    },
    { label: 'Delivery Charges', value: inr(stats.deliveryCharges), icon: MdLocalShipping, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: 'Charged to customers' },
    { label: 'AWB Cost', value: inr(stats.awbCost), icon: MdSavings, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: 'Billed by Shiprocket' },
    { label: 'Net Revenue', value: inr(stats.netRevenue), icon: MdInsights, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: 'Revenue minus AWB cost' },
  ];

  const activeFilterCount = [from || to, search, profitType !== 'all', sortBy !== 'date_desc'].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Sales &amp; Revenue Report</Typography>
          <Typography sx={{ fontSize: 14, color: '#64748b', fontFamily: FONT }}>
            Delivered orders whose return period has completed
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>
            Note
          </Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>
            CSV
          </Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>
            Excel
          </Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>
            Print / PDF
          </Button>
        </Box>
      </Box>

      {/* Stat cards */}
      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>
            Reset
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2.8}>
            <TextField
              label="Search order no. / customer"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField select label="Profit / Loss" size="small" fullWidth value={profitType} onChange={(e) => { setProfitType(e.target.value); setPage(0); }}>
              <MenuItem value="all">All orders</MenuItem>
              <MenuItem value="profit">Profitable only</MenuItem>
              <MenuItem value="loss">Loss only</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={2.4}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }}>
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Order Details</Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} orders</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                    sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    No orders match the selected filters.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow key={r.orderNumber || idx} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' } }}>
                  {COLUMNS.map((c) => {
                    if (c.key === 'profit') {
                      const positive = r.profit >= 0;
                      return (
                        <TableCell key={c.key} align="right">
                          <Chip
                            size="small"
                            icon={positive ? <MdTrendingUp /> : <MdTrendingDown />}
                            label={`${positive ? '+' : ''}${inr(r.profit)}`}
                            sx={{
                              fontWeight: 700,
                              fontFamily: FONT,
                              background: positive ? '#dcfce7' : '#fee2e2',
                              color: positive ? '#15803d' : '#b91c1c',
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                        </TableCell>
                      );
                    }
                    const value = c.format ? c.format(r[c.key]) : r[c.key] ?? '-';
                    return (
                      <TableCell
                        key={c.key}
                        align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                        sx={{
                          fontFamily: FONT,
                          whiteSpace: 'nowrap',
                          fontWeight: c.key === 'orderNumber' || c.key === 'netRevenue' ? 700 : 500,
                          color: c.key === 'netRevenue' ? '#0f172a' : '#334155',
                        }}
                      >
                        {c.key === 'customer' ? (
                          <Tooltip title={r.customerEmail || ''} placement="top" arrow>
                            <span>{value}</span>
                          </Tooltip>
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesRevenueReport;
